import { init, setGroup, setOptOut, track } from '@amplitude/analytics-browser'

import { useAdmin } from '@vtex/raccoon-next'
import { useAdminUser } from 'hooks'
import { useEffect, useState } from 'react'

type PluginState = 'initializing' | 'ready'

type Event = {
  event: string
  properties: Record<string, any>
}

const PRODUCTION_API_KEY = process.env.NEXT_PUBLIC_AMPLITUDE_PRODUCTION_API_KEY

const DEVELOPMENT_API_KEY =
  process.env.NEXT_PUBLIC_AMPLITUDE_DEVELOPMENT_API_KEY

export function useAmplitudePlugin() {
  const [pluginState, setPluginState] = useState<PluginState>('initializing')
  const [enqueuedEvents, setEnqueuedEvents] = useState<Event[]>([])

  const { account, production } = useAdmin()

  const { data, isError } = useAdminUser()

  const API_KEY = production ? PRODUCTION_API_KEY : DEVELOPMENT_API_KEY

  const enablePlugin = !!API_KEY

  useEffect(() => {
    if (!enablePlugin || !data) return

    const { id: userId, internalUser } = data

    init(API_KEY, { userId })

    setGroup('Account', account)
    setGroup('Account Type', 'VTEX Platform')
    setOptOut(production && internalUser)

    setPluginState('ready')
  }, [API_KEY, account, data, enablePlugin, production])

  useEffect(() => {
    if (pluginState === 'ready') {
      for (const { event, properties } of enqueuedEvents) {
        track(event, properties)
      }
      enqueuedEvents.length && setEnqueuedEvents([])
    }
  }, [pluginState, enqueuedEvents])

  return {
    name: 'amplitude-plugin',

    track: ({ payload }) => {
      if (!enablePlugin || isError) return

      if (pluginState === 'initializing') {
        setEnqueuedEvents((queue) => {
          const newQueue = [...queue, payload]

          return newQueue
        })

        return
      }

      track(payload.event, payload.properties)
    },
  }
}
